import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validateItalianPhone } from "@/lib/phoneValidation";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatTranscriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messages: Message[];
}

const schema = z.object({
  firstName: z.string().trim().min(2).max(50),
  lastName: z.string().trim().min(2).max(50),
  email: z.string().trim().email().max(255),
  phone: z.string().trim().min(6).max(30).refine((v) => validateItalianPhone(v).isValid || /^\+?[\d\s()-]{6,}$/.test(v), {
    message: "Numero non valido",
  }),
  nationality: z.string().trim().min(2).max(60),
  city: z.string().trim().min(2).max(100),
  minBudget: z.string().refine((v) => !isNaN(parseInt(v)) && parseInt(v) >= 0),
  maxBudget: z.string().refine((v) => !isNaN(parseInt(v)) && parseInt(v) > 0),
});

type FormData = z.infer<typeof schema>;

export const ChatTranscriptDialog = ({ open, onOpenChange, messages }: ChatTranscriptDialogProps) => {
  const { i18n } = useTranslation();
  const { toast } = useToast();
  const isEn = i18n.language?.startsWith("en");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: "", lastName: "", email: "", phone: "", nationality: "", city: "", minBudget: "", maxBudget: "" },
  });

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("send-chat-transcript", {
        body: {
          ...data,
          minBudget: parseInt(data.minBudget),
          maxBudget: parseInt(data.maxBudget),
          messages,
          language: isEn ? "en" : "it",
        },
      });
      if (error) throw error;
      setSuccess(true);
      toast({
        title: isEn ? "Email sent!" : "Email inviata!",
        description: isEn ? "Check your inbox for the transcript." : "Controlla la tua casella per la trascrizione.",
      });
      setTimeout(() => {
        onOpenChange(false);
        setSuccess(false);
        form.reset();
      }, 2000);
    } catch (e: any) {
      console.error(e);
      toast({
        title: isEn ? "Error" : "Errore",
        description: isEn ? "Could not send. Try again." : "Invio non riuscito. Riprova.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            {isEn ? "Receive transcript by email" : "Ricevi la trascrizione via email"}
          </DialogTitle>
          <DialogDescription>
            {isEn
              ? "Fill in your details to receive the full chat transcript. We'll also activate alerts for new listings matching your search."
              : "Compila i tuoi dati per ricevere la trascrizione completa. Attiveremo anche avvisi sui nuovi annunci compatibili con la tua ricerca."}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <CheckCircle className="h-12 w-12 text-green-600" />
            <p className="text-sm text-center font-medium">
              {isEn ? "Transcript sent to your email!" : "Trascrizione inviata alla tua email!"}
            </p>
          </div>
        ) : (
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName">{isEn ? "First name" : "Nome"} *</Label>
                <Input id="firstName" {...form.register("firstName")} />
              </div>
              <div>
                <Label htmlFor="lastName">{isEn ? "Last name" : "Cognome"} *</Label>
                <Input id="lastName" {...form.register("lastName")} />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" {...form.register("email")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="phone">{isEn ? "Phone" : "Telefono"} *</Label>
                <Input id="phone" {...form.register("phone")} placeholder="+39..." />
              </div>
              <div>
                <Label htmlFor="nationality">{isEn ? "Nationality" : "Nazionalità"} *</Label>
                <Input id="nationality" {...form.register("nationality")} placeholder={isEn ? "Italian" : "Italiana"} />
              </div>
            </div>
            <div>
              <Label htmlFor="city">{isEn ? "Search city" : "Città di ricerca"} *</Label>
              <Input id="city" {...form.register("city")} placeholder={isEn ? "e.g. Milan" : "es. Milano"} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="minBudget">{isEn ? "Min rent (€)" : "Affitto min (€)"} *</Label>
                <Input id="minBudget" type="number" min="0" {...form.register("minBudget")} />
              </div>
              <div>
                <Label htmlFor="maxBudget">{isEn ? "Max rent (€)" : "Affitto max (€)"} *</Label>
                <Input id="maxBudget" type="number" min="0" {...form.register("maxBudget")} />
              </div>
            </div>

            {Object.keys(form.formState.errors).length > 0 && (
              <p className="text-xs text-destructive">
                {isEn ? "Please fill all fields correctly." : "Compila tutti i campi correttamente."}
              </p>
            )}

            <p className="text-[11px] text-muted-foreground leading-relaxed">
              {isEn
                ? "By submitting, you consent to receive the transcript and listing alerts. AI responses are not binding for Domus Flexi — please refer to "
                : "Inviando, acconsenti a ricevere la trascrizione e gli avvisi sui nuovi annunci. Le risposte dell'AI non sono vincolanti per Domus Flexi — fai riferimento a "}
              <a href="https://www.domusflexi.com" target="_blank" rel="noreferrer" className="underline text-primary">www.domusflexi.com</a>
              {isEn ? " for terms and conditions." : " per termini e condizioni."}
            </p>

            <Button type="submit" disabled={submitting} className="w-full">
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {isEn ? "Sending…" : "Invio…"}</>
              ) : (
                <><Mail className="h-4 w-4 mr-2" /> {isEn ? "Send transcript" : "Invia trascrizione"}</>
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
